import React, { useCallback, useContext, useMemo, useState } from "react";
import "./BoardMenu.css";
import BoardMenuColumn from "../BoardMenuColumn/BoardMenuColumn";
import ExitCross from "../../../assets/cancelGreey.svg";
import DeleteBoardBlue from "../../../assets/deleteElementGray.svg";
import DeleteBoardGray from "../../../assets/grayDelete.svg";
import EditBoardBlue from "../../../assets/edit.svg";
import EditBoardGray from "../../../assets/editButtonGray.svg";
import { AppContext } from "../../../App";
import { Context } from "../../../index";
import { IFunctionPermissions } from "../../../common/api_interfaces/role/IFunctionPermissions";
import { hasPermission } from "../../../helpers/hasPermisson";
import { BoardContext } from "../../../contexts/BoardContext";
import CreateColumnForm from "../CreateColumnForm/CreateColumnForm";
import { useNavigate } from "react-router-dom";
import { systemFunctions } from "../../../common/enums/systemFunctions";
import { accessPermission } from "../../../common/enums/accessPermission";

export interface IBoardProps {
    userAccess: IFunctionPermissions[];
}

const BoardMenu: React.FC<IBoardProps> = ({ userAccess }) => {
    const boardContext = useContext(BoardContext);
    const { showToast, getData } = useContext(AppContext);

    const [previewColumnIndex, setPreviewColumnIndex] = useState<number | undefined>();
    const [dragEventTarget, setDragEventTarget] = useState<any>();
    const [editNameMode, setEditNameMode] = useState<boolean>(false);
    const [addColumnMode, setAddColumnMode] = useState<boolean>(false);
    const [nameError, setNameError] = useState<string>();

    const { editNameBoard, delBoard, addColumn } = useMemo(
        () => ({
            editNameBoard: hasPermission(userAccess, "BoardAction", "edit"),
            delBoard:
                hasPermission(userAccess, "BoardAction", "delete") &&
                (boardContext.isProxyBoard
                    ? hasPermission(userAccess, systemFunctions.ColumnProxyAction, accessPermission.delete)
                    : true),
            addColumn: hasPermission(userAccess, "ColumnAction", "create"),
        }),
        [userAccess, boardContext.isProxyBoard]
    );

    const editIcon = useMemo(() => (editNameBoard ? EditBoardBlue : EditBoardGray), [editNameBoard]);
    const deleteIcon = useMemo(() => (delBoard ? DeleteBoardBlue : DeleteBoardGray), [delBoard]);

    const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragEventTarget(e.target);
    }, []);

    const handleDragLeave = useCallback(
        (e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            if (dragEventTarget !== e.target) {
                removePreview();
            }
        },
        [dragEventTarget]
    );

    const removePreview = useCallback(() => {
        const elements = document.querySelectorAll(".column-preview");
        elements.forEach((element) => element.remove());
        setPreviewColumnIndex(undefined);
    }, []);

    const createPreviewElement = useCallback(() => {
        const newElement = document.createElement("div");
        newElement.className = "column-preview";
        newElement.innerHTML = "<div><div>";
        newElement.style.backgroundColor = "#149cce6b";
        newElement.style.padding = "25px";
        newElement.style.borderRadius = "6px";
        newElement.style.pointerEvents = "none";
        return newElement;
    }, []);

    const handleDragOver = useCallback(
        (e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();

            const droppedPosition = e.clientY;
            let insertIndex = boardContext.columns.length;

            for (let i = 0; i < boardContext.columns.length; i++) {
                const columnElement = document.getElementById(`column-${boardContext.columns[i].id}`);
                if (columnElement) {
                    const rect = columnElement.getBoundingClientRect();
                    const midY = rect.top + rect.height / 2;
                    if (droppedPosition < midY) {
                        insertIndex = i;
                        break;
                    }
                }
            }

            if (previewColumnIndex === insertIndex) return;

            removePreview();

            if (insertIndex < boardContext.columns.length) {
                const columnElement = document.getElementById(`column-${boardContext.columns[insertIndex].id}`);
                if (columnElement) {
                    const newElement = createPreviewElement();
                    columnElement.insertAdjacentElement("beforebegin", newElement);
                    setPreviewColumnIndex(insertIndex);
                }
            } else if (boardContext.columns.length > 0) {
                const columnElement = document.getElementById(
                    `column-${boardContext.columns[boardContext.columns.length - 1].id}`
                );
                if (columnElement) {
                    const newElement = createPreviewElement();
                    columnElement.insertAdjacentElement("afterend", newElement);
                    setPreviewColumnIndex(insertIndex);
                }
            }
        },
        [boardContext.columns, previewColumnIndex, removePreview, createPreviewElement]
    );

    const handleOnDrop = useCallback(
        (e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            try {
                const { columnId } = JSON.parse(e.dataTransfer.getData("application/json"));
                const droppedPosition = e.clientY;
                const movedColumn = boardContext.columns.find((c) => c.id === columnId);

                if (!movedColumn) return;

                let insertIndex = boardContext.columns.length;
                const currentIndex = boardContext.columns.indexOf(movedColumn);

                for (let i = 0; i < boardContext.columns.length; i++) {
                    const columnElement = document.getElementById(`column-${boardContext.columns[i].id}`);
                    if (columnElement) {
                        const rect = columnElement.getBoundingClientRect();
                        const midY = rect.top + rect.height / 2;
                        if (droppedPosition < midY) {
                            insertIndex = i;
                            break;
                        }
                    }
                }

                if (insertIndex !== currentIndex) insertIndex--;
                if (insertIndex < currentIndex) insertIndex++;

                if (insertIndex === currentIndex) {
                    removePreview();
                    return;
                }

                boardContext.handleMoveColumn(columnId, insertIndex);
                removePreview();
            } catch (err) {
                removePreview();
            }
        },
        [boardContext.columns, boardContext.handleMoveColumn, removePreview]
    );

    const handleDragEnd = useCallback(() => {
        removePreview();
    }, [removePreview]);

    const validateName = useCallback((name: string): boolean => {
        const nameErr = !name || !name.trim().length ? "Название доски не заполнено" : undefined;
        setNameError(nameErr);
        return nameErr !== undefined;
    }, []);

    const handleNameChange = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter") {
                const value = (e.target as HTMLInputElement).value;
                if (!validateName(value)) {
                    boardContext.handleRenameBoard(value);
                    setEditNameMode(false);
                }
            }
        },
        [validateName, boardContext.handleRenameBoard]
    );

    const toggleEditNameMode = useCallback(() => {
        if (editNameBoard) {
            setEditNameMode((prev) => !prev);
            setNameError(undefined);
        } else {
            showToast("У вас нет прав для редактирования названия!");
        }
    }, [editNameBoard, showToast]);

    const handleDeleteBoardWithCheck = useCallback(async () => {
        if (delBoard) {
            boardContext.handleDeleteBoard();
            await getData();
        } else {
            showToast("У вас нет прав для удаления доски!");
        }
    }, [delBoard, boardContext.handleDeleteBoard, showToast]);

    const toggleAddColumnMode = useCallback(() => {
        if (addColumn) {
            setAddColumnMode((prev) => !prev);
        } else {
            showToast("У вас нет прав для создания колонки!");
        }
    }, [addColumn, showToast]);

    const addColumnButtonStyle = useMemo(
        () => ({
            background: addColumn ? undefined : "#75848A",
        }),
        [addColumn]
    );

    const columnsList = useMemo(
        () =>
            boardContext.columns.map((c, index) => (
                <BoardMenuColumn
                    key={c.id}
                    column={c}
                    onColumnDragEnd={handleDragEnd}
                    draggable={index !== 0}
                    deletable={index !== 0}
                    userAccess={userAccess}
                />
            )),
        [boardContext.columns, handleDragEnd, userAccess]
    );

    return (
        <div className="board-menu-overlay">
            <div className="board-menu-container" onClick={(e) => e.stopPropagation()}>
                <div className="board-name-row">
                    <div className="board-name-row__name">
                        {editNameMode ? (
                            <input defaultValue={boardContext.boardName} onKeyDown={handleNameChange} autoFocus />
                        ) : (
                            <h1 title={boardContext.boardName}>{boardContext.boardName}</h1>
                        )}
                        <button
                            className="board-menu-overlay__button"
                            onClick={toggleEditNameMode}
                            title={editNameBoard ? "Редактировать название" : "Нет прав для редактирования"}
                        >
                            <img src={editIcon} alt="Edit" />
                        </button>
                        <button
                            className="board-menu-overlay__button"
                            onClick={handleDeleteBoardWithCheck}
                            title={delBoard ? "Удалить доску" : "Нет прав для удаления"}
                        >
                            <img src={deleteIcon} alt="Delete" />
                        </button>
                    </div>
                    <button className="board-menu-overlay__button" onClick={boardContext.closeMenu} title="Закрыть">
                        <img src={ExitCross} alt="Close" />
                    </button>
                </div>

                {editNameMode && nameError && (
                    <p className="error_message" style={{ margin: "0px" }}>
                        {nameError}
                    </p>
                )}

                <div className="board-menu-overlay__button--crete">
                    <button style={addColumnButtonStyle} onClick={toggleAddColumnMode}>
                        {addColumnMode ? "Закрыть режим создания" : "Добавить колонку"}
                    </button>
                </div>

                {addColumnMode && <CreateColumnForm userAccess={userAccess} />}

                <div
                    className="board-menu-columns"
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDrop={handleOnDrop}
                    onDragLeave={handleDragLeave}
                >
                    {columnsList}
                </div>
            </div>
        </div>
    );
};

export default BoardMenu;
